/* global HSCore */

import React, { useContext, useEffect, useState, useRef } from "react";
import { Route, Routes, Navigate } from "react-router-dom";

import SignUp from "./SignUp";
import SignIn from "./SignIn";
import Verified from "./Verified";
import VerifyEmail from "./VerifyEmail";
import ResendEmail from "./ResendEmail";
import ForgotPassword from "./ForgotPassword";

import OcChattingDark from "../../images/oc-chatting-dark.svg";
import OcChattingLight from "../../images/oc-chatting.svg";
import fitbitGray from "../../images/fitbit-gray.svg";
import flowXo from "../../images/flow-xo-gray.svg";
import gitLab from "../../images/gitlab-gray.svg";
import layarGray from "../../images/layar-gray.svg";
import logoDark from "../../images/brand-logo-dark.svg";
import logoLight from "../../images/brand-logo-light.svg";
import ResetPassword from "./ResetPassword";
import LanguageContext from "../../utils/Contexts/LanguageContext";

function AuthMain() {
  const { t, changeLanguage, language, languageOptions } = useContext(LanguageContext);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    password: "",
  });

  const jsSelectInitRef = useRef(null);
  useEffect(() => {
    if (!jsSelectInitRef.current) {
      HSCore.components.HSTomSelect.init('.js-select');
      // eslint-disable-next-line react-hooks/exhaustive-deps
      jsSelectInitRef.current = true;
    }
  }, []);

  return (
    <>
      <header className="position-absolute top-0 start-0 end-0 mt-3 mx-3">
        <div className="d-flex d-lg-none justify-content-between">
          <a href="/sign-in">
            <img
              className="w-100"
              src={logoLight}
              alt="Description"
              data-hs-theme-appearance="default"
              style={{ minWidth: "12rem", maxWidth: "12rem" }}
            />
            <img
              className="w-100"
              src={logoDark}
              alt="Description"
              data-hs-theme-appearance="dark"
              style={{ minWidth: "12rem", maxWidth: "12rem" }}
            />
          </a>

          <div className="tom-select-custom tom-select-custom-end tom-select-custom-bg-transparent">
            <select
              className="js-select form-select form-select-sm form-select-borderless"
              data-hs-tom-select-options='{
                "searchInDropdown": false,
                "hideSearch": true,
                "dropdownWidth": "12rem"
              }'
              id="tomselect-2"
              value={language} // Use the context's language state
              onChange={(e) => changeLanguage(e.target.value)} // Use onChange
            >
              {Object.keys(languageOptions).map((lng) => (
                <option
                  key={lng}
                  value={lng}
                  data-option-template={`<span class="d-flex align-items-center">
                    <img class="avatar avatar-xss avatar-circle me-2" 
                        src="${languageOptions[lng].flag}" 
                        alt="${languageOptions[lng].name} Flag" 
                        width="16"/>
                    <span>${languageOptions[lng].name}</span>
                  </span>`}
                >
                  {languageOptions[lng].name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>
      <main id="content" role="main" className="auth-main pt-0">
        <div className="container-fluid px-3">
          <div className="row">
            <div className="col-lg-6 d-none d-lg-flex justify-content-center align-items-center min-vh-lg-100 position-relative bg-light px-0">
              <div className="position-absolute top-0 start-0 end-0 mt-3 mx-3">
                <div className="d-none d-lg-flex justify-content-between">
                  <a href="/sign-in">
                    <img
                      className="w-100"
                      src={logoLight}
                      alt="Description"
                      data-hs-theme-appearance="default"
                      style={{ minWidth: "12rem", maxWidth: "12rem" }}
                    />
                    <img
                      className="w-100"
                      src={logoDark}
                      alt="Description"
                      data-hs-theme-appearance="dark"
                      style={{ minWidth: "12rem", maxWidth: "12rem" }}
                    />
                  </a>

                  <div className="tom-select-custom tom-select-custom-end tom-select-custom-bg-transparent">
                    <select
                      className="js-select form-select form-select-sm form-select-borderless"
                      data-hs-tom-select-options='{
                        "searchInDropdown": false,
                        "hideSearch": true,
                        "dropdownWidth": "12rem"
                      }'
                      id="tomselect-2"
                      value={language} // Use the context's language state
                      onChange={(e) => changeLanguage(e.target.value)} // Use onChange
                    >
                      {Object.keys(languageOptions).map((lng) => (
                        <option
                          key={lng}
                          value={lng}
                          data-option-template={`<span class="d-flex align-items-center">
                            <img class="avatar avatar-xss avatar-circle me-2" 
                                src="${languageOptions[lng].flag}" 
                                alt="${languageOptions[lng].name} Flag" 
                                width="16"/>
                            <span>${languageOptions[lng].name}</span>
                          </span>`}
                        >
                          {languageOptions[lng].name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ maxWidth: "23rem" }}>
                <div className="text-center mb-5">
                  <img
                    className="img-fluid"
                    src={OcChattingLight}
                    alt="Description"
                    style={{ width: "12rem" }}
                    data-hs-theme-appearance="default"
                  />
                  <img
                    className="img-fluid"
                    src={OcChattingDark}
                    alt="Description"
                    style={{ width: "12rem" }}
                    data-hs-theme-appearance="dark"
                  />
                </div>

                <div className="mb-5">
                  <h2 className="display-5">
                    {t("Build digital products with:")}
                  </h2>
                </div>

                <ul className="list-checked list-checked-lg list-checked-primary list-py-2">
                  <li className="list-checked-item">
                    <span className="d-block fw-semibold mb-1">
                      {t("All-in-one tool")}
                    </span>
                    {t("Build, run, and scale your apps - end to end")}
                  </li>

                  <li className="list-checked-item">
                    <span className="d-block fw-semibold mb-1">
                      {t("Easily add & manage your services")}
                    </span>
                    {t(
                      "It brings together your tasks, projects, timelines, files and more"
                    )}
                  </li>
                </ul>

                <div className="row justify-content-between mt-5 gx-3">
                  <div className="col">
                    <img className="img-fluid" src={gitLab} alt="Logo" />
                  </div>

                  <div className="col">
                    <img className="img-fluid" src={fitbitGray} alt="Logo" />
                  </div>

                  <div className="col">
                    <img className="img-fluid" src={flowXo} alt="Logo" />
                  </div>

                  <div className="col">
                    <img className="img-fluid" src={layarGray} alt="Logo" />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-center align-items-center min-vh-lg-100">
              <Routes>
                <Route
                  path="/sign-up"
                  element={
                    <SignUp formData={formData} setFormData={setFormData} />
                  }
                />
                <Route
                  path="/sign-in"
                  element={
                    <SignIn formData={formData} setFormData={setFormData} />
                  }
                />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route
                  path="/verify-email"
                  element={<VerifyEmail formData={formData} />}
                />
                <Route
                  path="/resend-email"
                  element={
                    <ResendEmail formData={formData} setFormData={setFormData} />
                  }
                />
                <Route path="/verify/:token" element={<Verified />} />
                <Route
                  path="/reset-password/:token"
                  element={<ResetPassword />}
                />
                <Route path="*" element={<Navigate to="/sign-in" />} />
              </Routes>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default AuthMain;
